import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarDays, Hand, QrCode, Search } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { ChipGroup } from '@/components/app/chip-group'
import { EmptyState } from '@/components/app/empty-state'
import { ErrorBanner } from '@/components/app/error-banner'
import { Fab } from '@/components/app/fab'
import { type Feedback, FeedbackBanner } from '@/components/app/feedback-banner'
import { Field, fieldClass } from '@/components/app/field'
import { ListSkeleton } from '@/components/app/list-skeleton'
import { QrScanner } from '@/components/app/qr-scanner'
import { Screen } from '@/components/app/screen'
import { ScreenHeader } from '@/components/app/screen-header'
import { SubmitButton } from '@/components/app/submit-button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { apiGet, apiPost } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { SERVICE_TYPE_LABELS, SERVICE_TYPE_SHORT, SERVICE_TYPES } from '@/lib/constants'
import { formatDateFR } from '@/lib/format'
import { memberInitials, membersQuery } from '@/lib/queries/members'
import { recentServicesQuery, type Service } from '@/lib/queries/services'
import { canCreateService, canRecordAttendance } from '@/lib/roles'

export const Route = createFileRoute('/_staff/cultes/')({
  component: ServicesScreen,
})

const TYPE_OPTIONS = SERVICE_TYPES.map((value) => ({
  value,
  label: SERVICE_TYPE_SHORT[value],
}))

function ServicesScreen() {
  const { roles } = Route.useRouteContext()
  const [createOpen, setCreateOpen] = useState(false)
  const [selected, setSelected] = useState<Service | null>(null)

  const { data, isPending } = useQuery(recentServicesQuery(20))
  const total = data?.meta.total ?? 0

  return (
    <Screen withTabBar>
      <ScreenHeader title="Cultes" subtitle={data ? `${total} au total` : undefined} />

      <div className="px-5">
        {isPending && <ListSkeleton rows={6} />}
        {data?.data.length === 0 && <EmptyState message="Aucun culte enregistré." />}

        <ul className="flex flex-col gap-2">
          {data?.data.map((service) => (
            <li key={service.id}>
              <button
                type="button"
                onClick={() => setSelected(service)}
                className="flex w-full items-center gap-3 rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3 text-left"
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gold/15">
                  <CalendarDays size={18} color="#C9A84C" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-semibold text-chalk">
                    {SERVICE_TYPE_LABELS[service.type] ?? service.type}
                  </span>
                  <span className="block truncate text-[12px] text-steel">
                    {formatDateFR(service.date, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                    {service.theme ? ` · ${service.theme}` : ''}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {canCreateService(roles) && <Fab onClick={() => setCreateOpen(true)} label="Nouveau culte" />}

      <CreateServiceSheet open={createOpen} onOpenChange={setCreateOpen} />
      <ServiceDetailSheet
        service={selected}
        onClose={() => setSelected(null)}
        canRecord={canRecordAttendance(roles)}
      />
    </Screen>
  )
}

function CreateServiceSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [type, setType] = useState<string>('SUNDAY')
  const [theme, setTheme] = useState('')
  const [preacher, setPreacher] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      apiPost('services', {
        date,
        type,
        theme: theme.trim() || undefined,
        preacher: preacher.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      onOpenChange(false)
      setTheme('')
      setPreacher('')
      setError(null)
    },
    onError: async (err) => {
      setError(getApiErrorMessage(err, "Impossible d'enregistrer le culte."))
    },
  })

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (date.length === 10 && !mutation.isPending) mutation.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85dvh] border-navy-800 bg-navy-900">
        <SheetHeader>
          <SheetTitle className="text-chalk">Nouveau culte</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-y-auto px-5 pb-6">
          <Field label="Date">
            {/* Sélecteur natif : l'app native demande une saisie texte au
                format YYYY-MM-DD, source d'erreurs de frappe. */}
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={fieldClass}
            />
          </Field>

          <ChipGroup label="Type" options={TYPE_OPTIONS} value={type} onChange={setType} />

          <Field label="Thème (facultatif)">
            <input
              type="text"
              maxLength={200}
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className={fieldClass}
            />
          </Field>

          <Field label="Prédicateur (facultatif)">
            <input
              type="text"
              maxLength={100}
              value={preacher}
              onChange={(e) => setPreacher(e.target.value)}
              className={fieldClass}
            />
          </Field>

          <ErrorBanner message={error} />

          <SubmitButton disabled={date.length !== 10} loading={mutation.isPending}>
            Enregistrer
          </SubmitButton>
        </form>
      </SheetContent>
    </Sheet>
  )
}

type DetailMode = 'menu' | 'scan' | 'manual'

function ServiceDetailSheet({
  service,
  onClose,
  canRecord,
}: {
  service: Service | null
  onClose: () => void
  canRecord: boolean
}) {
  const [mode, setMode] = useState<DetailMode>('menu')

  function handleOpenChange(open: boolean) {
    if (!open) {
      // Repasser en `menu` démonte le scanner, donc libère la caméra.
      setMode('menu')
      onClose()
    }
  }

  if (!service) return null

  return (
    <Sheet open onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="max-h-[90dvh] border-navy-800 bg-navy-900">
        <SheetHeader>
          <SheetTitle className="text-chalk">
            {SERVICE_TYPE_LABELS[service.type] ?? service.type}
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 pb-6">
          <ServiceStats serviceId={service.id} />

          {!canRecord && (
            <EmptyState message="Vous n'êtes pas autorisé à enregistrer des présences." />
          )}

          {canRecord && mode === 'menu' && (
            <div className="flex flex-col gap-2">
              <ModeButton
                icon={<QrCode size={20} color="#C9A84C" aria-hidden="true" />}
                label="Scanner les QR codes"
                onClick={() => setMode('scan')}
              />
              <ModeButton
                icon={<Hand size={20} color="#C9A84C" aria-hidden="true" />}
                label="Saisie manuelle"
                onClick={() => setMode('manual')}
              />
            </div>
          )}

          {canRecord && mode === 'scan' && (
            <ScanPanel serviceId={service.id} onBack={() => setMode('menu')} />
          )}

          {canRecord && mode === 'manual' && (
            <ManualPanel serviceId={service.id} onBack={() => setMode('menu')} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function ServiceStats({ serviceId }: { serviceId: string }) {
  const { data } = useQuery({
    queryKey: ['services', serviceId, 'stats'],
    queryFn: () => apiGet<{ totalAttendances: number }>(`services/${serviceId}/stats`),
  })

  return (
    <p className="text-[14px] text-steel">
      <span className="text-[20px] font-bold text-gold">{data?.totalAttendances ?? '—'}</span>{' '}
      présence{(data?.totalAttendances ?? 0) > 1 ? 's' : ''} enregistrée
      {(data?.totalAttendances ?? 0) > 1 ? 's' : ''}
    </p>
  )
}

function ModeButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-[14px] border border-navy-800 px-4 py-3.5 text-left text-[15px] font-semibold text-chalk"
    >
      {icon}
      {label}
    </button>
  )
}

function ScanPanel({ serviceId, onBack }: { serviceId: string; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [feedback, setFeedback] = useState<Feedback | null>(null)

  async function handleDetect(qrCode: string) {
    setFeedback({ kind: 'pending', message: 'Vérification…' })

    try {
      const res = await apiPost<{ member: { firstName: string; lastName: string } }>(
        `services/${serviceId}/attendances/qr`,
        { qrCode },
      )
      queryClient.invalidateQueries({ queryKey: ['services', serviceId, 'stats'] })
      setFeedback({
        kind: 'success',
        message: `${res.member.firstName} ${res.member.lastName} — présent`,
      })
    } catch (err) {
      setFeedback({ kind: 'error', message: getApiErrorMessage(err, 'QR non reconnu.') })
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <QrScanner onDetect={handleDetect} variant="inline" />
      <FeedbackBanner feedback={feedback} idleMessage="Pointez la caméra vers le QR du fidèle." />
      <BackButton onClick={onBack} />
    </div>
  )
}

function ManualPanel({ serviceId, onBack }: { serviceId: string; onBack: () => void }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const [feedback, setFeedback] = useState<Feedback | null>(null)

  const { data } = useQuery({
    ...membersQuery(debouncedSearch, 20),
    enabled: debouncedSearch.trim().length > 0,
  })

  const mutation = useMutation({
    mutationFn: (memberId: string) =>
      apiPost(`services/${serviceId}/attendances`, { memberId, method: 'MANUAL' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services', serviceId, 'stats'] })
    },
  })

  async function markPresent(memberId: string, name: string) {
    setFeedback({ kind: 'pending', message: 'Enregistrement…' })
    try {
      await mutation.mutateAsync(memberId)
      setFeedback({ kind: 'success', message: `${name} — présent` })
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: getApiErrorMessage(err, "Impossible d'enregistrer la présence."),
      })
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search
          size={18}
          color="#8A9BB0"
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un fidèle"
          aria-label="Rechercher un fidèle"
          className="h-13 w-full rounded-[14px] border border-navy-800 bg-navy-950 py-3 pr-4 pl-11 text-[16px] text-chalk placeholder:text-mist focus:border-gold focus:outline-none"
        />
      </div>

      <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
        {data?.data.map((member) => (
          <li key={member.id}>
            <button
              type="button"
              onClick={() => markPresent(member.id, `${member.firstName} ${member.lastName}`)}
              className="flex w-full items-center gap-3 rounded-[14px] border border-navy-800 px-4 py-2.5 text-left"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gold/15 text-[13px] font-semibold text-gold">
                {memberInitials(member)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold text-chalk">
                  {member.firstName} {member.lastName}
                </span>
                <span className="block text-[12px] text-steel">{member.memberNumber}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      <FeedbackBanner
        feedback={feedback}
        idleMessage={search ? undefined : 'Saisissez un nom pour rechercher.'}
      />
      <BackButton onClick={onBack} />
    </div>
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[14px] border border-navy-800 py-3 text-[15px] text-steel"
    >
      Retour
    </button>
  )
}
